import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import "@/App.css";
import "@/i18n";
import { DarkModeProvider } from "@/contexts/DarkModeContext";
import { UserProvider, useUser } from "@/contexts/UserContext";
import FeedbackWidget from "@/components/FeedbackWidget";

// Pages
import LoginPage from "@/pages/LoginPage";
import LandingPage from "@/pages/LandingPage";
import OnboardingPage from "@/pages/OnboardingPage";
import WelcomePage from "@/pages/WelcomePage";
import DashboardPage from "@/pages/DashboardPage";
import BookDetailPage from "@/pages/BookDetailPage";
import ChapterEditorPage from "@/pages/ChapterEditorPage";
import SettingsPage from "@/pages/SettingsPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

// Set up axios to use the legacy bearer token only when present.
// Web sessions use the HttpOnly session cookie set by the API.
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 and 402 responses globally
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/session')) {
      localStorage.removeItem('auth_token');
      globalUser = null;
      globalAuthChecked = false;
    }

    if (error.response?.status === 402) {
      const detail = error.response.data?.detail;
      const message = typeof detail === 'object'
        ? detail.message
        : "You've reached your plan limit.";
      toast(message, {
        duration: 8000,
        action: {
          label: 'Upgrade',
          onClick: () => { window.location.href = '/settings?tab=subscription'; }
        },
      });
    }

    return Promise.reject(error);
  }
);

// Simple auth state management
let globalUser = null;
let globalAuthChecked = false;

function App() {
  return (
    <DarkModeProvider>
      <UserProvider>
        <BrowserRouter>
          <AuthHandler />
        </BrowserRouter>
      </UserProvider>
    </DarkModeProvider>
  );
}

function AuthHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const [authState, setAuthState] = useState('checking');
  const { user, setUser, isAdmin, setIsAdmin } = useUser();
  const processedHashRef = useRef(null);
  const checkedAuthRef = useRef(false);
  const isExchangingRef = useRef(false);

  useEffect(() => {
    const hash = window.location.hash;

    // CASE 1: We have a token in the hash from Google OAuth callback
    if (hash && hash.includes('token=')) {
      const token = hash.split('token=')[1]?.split('&')[0];

      // Skip if we already processed this exact token
      if (token && processedHashRef.current !== token) {
        processedHashRef.current = token;
        isExchangingRef.current = true;
        setAuthState('exchanging');

        // Store token and clear hash from URL immediately
        localStorage.setItem('auth_token', token);
        const intendedPath = window.location.pathname;
        window.history.replaceState(null, '', window.location.pathname + window.location.search);

        axios.get(`${API}/auth/me`, { timeout: 10000 })
        .then(response => {
          isExchangingRef.current = false;
          globalUser = response.data;
          globalAuthChecked = true;
          setUser(response.data);
          setAuthState('authenticated');
          // If the user came through onboarding, land on the welcome page
          const onboardingSessionId = localStorage.getItem('onboarding_session_id');
          if (onboardingSessionId) {
            navigate('/welcome', { replace: true });
          } else {
            // Navigate to the originally intended path; fall back to /dashboard
            // if the path is empty, root, or a pre-auth page.
            const preAuthPaths = ['/', '/login', '/landing', '/privacy', '/terms'];
            const target = (intendedPath && !preAuthPaths.includes(intendedPath))
              ? intendedPath
              : '/dashboard';
            navigate(target, { replace: true });
          }
          // Check admin status
          axios.get(`${API}/admin/me`).then(r => setIsAdmin(r.data?.is_admin === true)).catch(() => {});
        })
        .catch(error => {
          isExchangingRef.current = false;
          console.error('Auth: Token validation FAILED:', error.response?.data || error.message);
          localStorage.removeItem('auth_token');
          globalUser = null;
          globalAuthChecked = true;
          setAuthState('unauthenticated');
          navigate('/login', { replace: true });
        });

        return;
      }
    }
    
    // CASE 2: No hash - check existing auth (only once)
    if (checkedAuthRef.current) return;
    
    // Don't interrupt an in-progress session exchange (handles React Strict Mode double-mount)
    if (isExchangingRef.current) return;
    
    checkedAuthRef.current = true;
    
    // If we already have a global user, use it
    if (globalAuthChecked && globalUser) {
      setUser(globalUser);
      setAuthState('authenticated');
      return;
    }
    
    // Validate cookie-backed web session or legacy bearer token fallback with server.
    axios.get(`${API}/auth/me`, { timeout: 10000 })
    .then(response => {
      globalUser = response.data;
      globalAuthChecked = true;
      setUser(response.data);
      setAuthState('authenticated');
      // Check admin status
      axios.get(`${API}/admin/me`).then(r => setIsAdmin(r.data?.is_admin === true)).catch(() => {});
    })
    .catch(() => {
      localStorage.removeItem('auth_token');
      globalUser = null;
      globalAuthChecked = true;
      setAuthState('unauthenticated');
    });
    
  }, [navigate, location, setIsAdmin, setUser]);

  // Deep link listener for native Android/iOS — handles OAuth callback via custom scheme
  useEffect(() => {
    let listener = null;
    (async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (!Capacitor.isNativePlatform()) return;
        const { App: CapApp } = await import('@capacitor/app');
        const { Browser } = await import('@capacitor/browser');
        listener = await CapApp.addListener('appUrlOpen', async (data) => {
          const hashIdx = data.url.indexOf('#token=');
          if (hashIdx === -1) return;
          const token = data.url.substring(hashIdx + 7).split('&')[0];
          if (!token) return;
          try { await Browser.close(); } catch {}
          localStorage.setItem('auth_token', token);
          try {
            const res = await axios.get(`${API}/auth/me`, { timeout: 10000 });
            globalUser = res.data;
            globalAuthChecked = true;
            setUser(res.data);
            setAuthState('authenticated');
            navigate('/dashboard', { replace: true });
            axios.get(`${API}/admin/me`).then(r => setIsAdmin(r.data?.is_admin === true)).catch(() => {});
          } catch {
            localStorage.removeItem('auth_token');
            globalUser = null;
            globalAuthChecked = true;
            setAuthState('unauthenticated');
            navigate('/login', { replace: true });
          }
        });
      } catch {}
    })();
    return () => { listener?.remove?.(); };
  }, [navigate, setIsAdmin, setUser]);

  // Show loading while checking/exchanging
  if (authState === 'checking' || authState === 'exchanging') {
    return (
      <div className="story-page flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-[var(--story-primary)]/20 border-t-[var(--story-primary)]"></div>
          <p className="story-body">
            {authState === 'exchanging' ? 'Completing sign in...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  // Render routes based on auth state
  const isAuthenticated = authState === 'authenticated' && user;

  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <OnboardingPage />
        } />
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/login" element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
        } />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        
        {/* Welcome page — post-onboarding signup destination */}
        <Route path="/welcome" element={
          isAuthenticated ? <WelcomePage /> : <Navigate to="/" replace />
        } />

        {/* Protected routes */}
        <Route path="/dashboard" element={
          isAuthenticated ? <DashboardPage /> : <Navigate to="/login" replace />
        } />
        <Route path="/books/:bookId" element={
          isAuthenticated ? <BookDetailPage /> : <Navigate to="/login" replace />
        } />
        <Route path="/chapters/:chapterId" element={
          isAuthenticated ? <ChapterEditorPage /> : <Navigate to="/login" replace />
        } />
        <Route path="/settings" element={
          isAuthenticated ? <SettingsPage /> : <Navigate to="/login" replace />
        } />
        <Route path="/admin" element={
          isAuthenticated && isAdmin ? <AdminDashboardPage /> : isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
        } />
      </Routes>
      {isAuthenticated && <FeedbackWidget />}
    </>
  );
}

export default App;
